import React, { useState } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Input,
} from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import authService from '../../../../services/authService';

interface SystemPasswordDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (newPassword: string) => void;
  user: string;
}

const SystemPasswordDialog: React.FC<SystemPasswordDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  user,
}) => {
  const [step, setStep] = useState<'verify' | 'new'>('verify');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');

  const handleVerifyPassword = async () => {
    try {
      await authService.checkPassword(user, currentPassword);
      setStep('new');
      setError('');
    } catch (err) {
      setError('Felaktigt lösenord');
    }
  };

  const handleSaveNewPassword = () => {
    if (newPassword.trim()) {
      onSave(newPassword.trim());
      setStep('verify');
      setCurrentPassword('');
      setNewPassword('');
      setError('');
    }
  };

  const handleClose = () => {
    setStep('verify');
    setCurrentPassword('');
    setNewPassword('');
    setError('');
    onClose();
  };

  return (
    <Dialog modalType='alert' open={isOpen} onOpenChange={(_, data) => !data.open && handleClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                onClick={handleClose}
              />
            }
          >
            {step === 'verify' ? 'Vänligen ange ditt lösenord' : 'Ange nytt system lösenord'}
          </DialogTitle>
          <DialogContent>
            <div className="space-y-4">
              {step === 'verify' ? (
                <div>
                  <label className="block text-sm font-medium mb-2">Nuvarande lösenord</label>
                  <Input
                    type="password"
                    className="w-full"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleVerifyPassword()}
                    autoFocus
                  />
                  {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium mb-2">Nytt system lösenord</label>
                  <Input
                    type="password"
                    className="w-full"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSaveNewPassword()}
                    autoFocus
                  />
                </div>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={handleClose}>
              Avbryt
            </Button>
            {step === 'verify' ? (
              <Button
                appearance="primary"
                onClick={handleVerifyPassword}
                disabled={!currentPassword.trim()}
              >
                Verifiera
              </Button>
            ) : (
              <Button
                appearance="primary"
                onClick={handleSaveNewPassword}
                disabled={!newPassword.trim()}
              >
                Spara
              </Button>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default SystemPasswordDialog;
